// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminPromocodes\AdminPromocodes.jsx
// Название: Admin Promocodes Management
// Версия: 1.0.0
// Дата: 26.01.2026

import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import './AdminPromocodes.css';

const AdminPromocodes = () => {
  const [promocodes, setPromocodes] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, 
    available: 0, 
    assigned: 0, 
    redeemed: 0,
    mainCount: 0,
    referralCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/promocodes');
      if (res.data.success) {
        setPromocodes(res.data.promocodes || []);
        setStats(res.data.stats || {});
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: { label: 'Доступен', class: 'badge-success', icon: '✅' },
      assigned: { label: 'Выдан', class: 'badge-info', icon: '📤' },
      redeemed: { label: 'Использован', class: 'badge-primary', icon: '🎉' },
      expired: { label: 'Истёк', class: 'badge-danger', icon: '⏰' }
    };
    const badge = badges[status] || { label: status, class: 'badge-default', icon: '❓' };
    return <span className={`admin-badge ${badge.class}`}>{badge.icon} {badge.label}</span>;
  };

  const getTypeBadge = (type) => {
    const badges = {
      main: { label: 'Main', class: 'type-main', icon: '🎁' },
      referral: { label: 'Referral', class: 'type-referral', icon: '👥' }
    };
    const badge = badges[type] || { label: type, class: 'type-default', icon: '🎟️' };
    return <span className={`type-badge ${badge.class}`}>{badge.icon} {badge.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredPromocodes = promocodes.filter(promo => {
    // Status filter
    if (filter !== 'all' && promo.status !== filter) return false;
    
    // Type filter
    if (typeFilter !== 'all' && promo.type !== typeFilter) return false;
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        promo.code?.toLowerCase().includes(searchLower) ||
        promo.campaign_name?.toLowerCase().includes(searchLower) ||
        promo.brand_name?.toLowerCase().includes(searchLower) ||
        promo.assigned_to_email?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="admin-promocodes">
        <div className="admin-loading">⏳ Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="admin-promocodes">
      <div className="admin-page-header">
        <h1>🎟️ Промокоды</h1>
        <p>Управление промокодами кампаний</p>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-icon">🎟️</div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Всего</span>
          </div>
        </div>
        <div className="admin-stat-card success">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.available}</span>
            <span className="stat-label">Доступных</span>
          </div>
        </div>
        <div className="admin-stat-card info">
          <div className="stat-icon">📤</div>
          <div className="stat-info">
            <span className="stat-value">{stats.assigned}</span>
            <span className="stat-label">Выданных</span>
          </div>
        </div>
        <div className="admin-stat-card primary">
          <div className="stat-icon">🎉</div>
          <div className="stat-info">
            <span className="stat-value">{stats.redeemed}</span>
            <span className="stat-label">Использовано</span>
          </div>
        </div>
      </div>

      {/* Type Stats */}
      <div className="type-stats-row">
        <div className="type-stat-card main">
          <div className="type-stat-icon">🎁</div>
          <div className="type-stat-info">
            <span className="type-stat-value">{stats.mainCount || 0}</span>
            <span className="type-stat-label">Main (10 DAR)</span>
          </div>
        </div>
        <div className="type-stat-card referral">
          <div className="type-stat-icon">👥</div>
          <div className="type-stat-info">
            <span className="type-stat-value">{stats.referralCount || 0}</span>
            <span className="type-stat-label">Referral (5 DAR)</span>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="admin-toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Поиск по коду, бренду, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="admin-filters">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Все
          </button>
          <button 
            className={`filter-btn ${filter === 'available' ? 'active' : ''}`}
            onClick={() => setFilter('available')}
          >
            Доступные
          </button>
          <button 
            className={`filter-btn ${filter === 'assigned' ? 'active' : ''}`}
            onClick={() => setFilter('assigned')}
          >
            Выданные
          </button>
          <button 
            className={`filter-btn ${filter === 'redeemed' ? 'active' : ''}`}
            onClick={() => setFilter('redeemed')}
          >
            Использованные
          </button>
        </div>
        <div className="type-filters">
          <button 
            className={`type-btn ${typeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setTypeFilter('all')}
          >
            Все типы
          </button>
          <button 
            className={`type-btn main ${typeFilter === 'main' ? 'active' : ''}`}
            onClick={() => setTypeFilter('main')}
          >
            Main
          </button>
          <button 
            className={`type-btn referral ${typeFilter === 'referral' ? 'active' : ''}`}
            onClick={() => setTypeFilter('referral')}
          >
            Referral
          </button>
        </div>
      </div>

      {/* Promocodes Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Код</th>
              <th>Тип</th>
              <th>DAR</th>
              <th>Кампания / Бренд</th>
              <th>Статус</th>
              <th>Выдан</th>
              <th>Создан</th>
            </tr>
          </thead>
          <tbody>
            {filteredPromocodes.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-row">Нет промокодов</td>
              </tr>
            ) : (
              filteredPromocodes.map(promo => (
                <tr key={promo.id}>
                  <td>#{promo.id}</td>
                  <td>
                    <code className="promo-code">{promo.code}</code>
                  </td>
                  <td>{getTypeBadge(promo.type)}</td>
                  <td>
                    <span className="dar-amount">{promo.dar_amount} DAR</span>
                  </td>
                  <td>
                    <div className="campaign-cell">
                      <span className="campaign-name">{promo.campaign_name || '—'}</span>
                      <span className="brand-name">{promo.brand_name || '—'}</span>
                    </div>
                  </td>
                  <td>{getStatusBadge(promo.status)}</td>
                  <td>
                    {promo.assigned_to_email ? (
                      <div className="assigned-to">
                        <span className="assigned-email">{promo.assigned_to_email}</span>
                        <span className="assigned-date">{formatDate(promo.assigned_at)}</span>
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-muted">{formatDate(promo.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPromocodes;
